import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { SERVICES as DEFAULT_SERVICES } from "../data/services";
import { PROJECTS as DEFAULT_PROJECTS } from "../data/projects";

const API = process.env.REACT_APP_BACKEND_URL;

// Media stored in object storage is referenced by path ("luminart/...");
// bundled or absolute URLs pass through unchanged.
export const mediaUrl = (src) => {
  if (!src) return src;
  if (src.startsWith("luminart/")) return `${API}/api/media/${src}`;
  return src;
};

const normalizeProject = (p) => {
  if (!p.media) return p; // bundled fallback shape
  return {
    id: p.id,
    title: p.title,
    location: p.location || "",
    tags: p.category ? [p.category] : [],
    desc: p.description || "",
    cover: p.cover ? mediaUrl(p.cover) : undefined,
    images: (p.media || []).map((m) => mediaUrl(m.src)),
    published: p.published,
    order: p.order,
  };
};

const normalizeService = (s) => ({
  slug: s.slug,
  title: s.title,
  desc: s.desc || "",
  intro: s.intro || "",
  img: mediaUrl(s.image || s.img),
});

const DEFAULTS = {
  services: DEFAULT_SERVICES.map(normalizeService),
  projects: DEFAULT_PROJECTS,
  text: {},
};

const ContentCtx = createContext({ ...DEFAULTS, loaded: false });

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/content/bootstrap`)
      .then(({ data }) => {
        setContent({
          services: (() => {
            const list = (
              data.services && data.services.length
                ? data.services
                : DEFAULT_SERVICES
            ).map(normalizeService);
            const seen = new Set();
            return list.filter((s) => !seen.has(s.slug) && seen.add(s.slug));
          })(),
          projects:
            data.projects && data.projects.length
              ? data.projects.map(normalizeProject)
              : DEFAULTS.projects,
          text: data.text || {},
          loaded: true,
        });
      })
      .catch(() => setContent({ ...DEFAULTS }));
  }, []);

  const value = content ? { ...content, loaded: true } : { ...DEFAULTS, loaded: false };
  return (
    <ContentCtx.Provider value={value}>{children}</ContentCtx.Provider>
  );
};

export const useContent = () => useContext(ContentCtx);

export const useSiteText = () => {
  const { text } = useContent();
  return (key, fallback) => text[key] || fallback;
};
